import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
    Table, TableHead, TableBody, TableRow, TableCell, Paper,
    TableContainer, Button, Box, MenuItem, Select, FormControl,
    InputLabel, TextField, Chip, Typography, CircularProgress,
    TablePagination
} from '@mui/material';
import client from '../../../../../API';
import EditCreditsDialog from './editCreditsAdmin';
import EditUserDialog from './editUserAdmin';
import useAlertDialog from '../../../../../Components/useAlertDialog';
import AlertDialog from '../../../../../Components/AlertDialog';
import { toast } from 'sonner';

const UnifiedUserAdminView = () => {
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [userToEdit, setUserToEdit] = useState(null);
    const [isCreatorUser, setIsCreatorUser] = useState(false);

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    // Filter states - DEFAULT: Sort by created_at (desc)
    const [userTypeFilter, setUserTypeFilter] = useState('all');
    const [sortField, setSortField] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [searchTerm, setSearchTerm] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const alertHk = useAlertDialog();

    // Build query params for API call
    const queryParams = useMemo(() => {
        const params = {
            page,
            rows: rowsPerPage,
        };

        if (userTypeFilter !== 'all') {
            params.user_type = userTypeFilter;
        }

        if (statusFilter !== 'all') {
            params.status = statusFilter;
        }

        if (searchTerm.trim()) {
            params.search = searchTerm.trim();
        }

        if (companyFilter) {
            params.company = companyFilter;
        }

        return params;
    }, [page, rowsPerPage, userTypeFilter, statusFilter, searchTerm, companyFilter]);

    // Fetch data with pagination
    const { data: apiResponse, error, refetch, isLoading } = useQuery({
        queryKey: ['admin', 'unifiedUsers', queryParams],
        queryFn: () => client.users.unifiedUserAdminFetch(queryParams),
        keepPreviousData: true, // Prevent UI flicker when paginating
        onError: (err) => {
            if (process.env.NODE_ENV === 'development') {
                console.log('Error fetching unified user data:', err);
            }
            toast.error('Failed to load user data', {
                description: err.message
            });
        }
    });

    // Extract data from API response
    const userData = apiResponse?.data || [];
    const totalCount = apiResponse?.total || 0;

    // Extract unique companies for filter dropdown
    const uniqueCompanies = useMemo(() => {
        if (!userData || userData.length === 0) return [];
        const companies = new Set();
        userData.forEach(user => {
            if (user.company_name) companies.add(user.company_name);
            if (user.manager) companies.add(user.manager);
        });
        return Array.from(companies).sort();
    }, [userData]);

    // Client-side sorting (for current page only)
    const sortedUsers = useMemo(() => {
        if (!userData) return [];

        const sorted = [...userData];

        sorted.sort((a, b) => {
            let aVal, bVal;
            let comparison = 0;

            switch (sortField) {
                case 'created_at':
                    aVal = new Date(a.created_at || 0);
                    bVal = new Date(b.created_at || 0);
                    comparison = sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
                    break;
                case 'last_login':
                    aVal = new Date(a.last_login || 0);
                    bVal = new Date(b.last_login || 0);
                    comparison = sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
                    if (comparison === 0) {
                        const aCreated = new Date(a.created_at || 0);
                        const bCreated = new Date(b.created_at || 0);
                        comparison = bCreated - aCreated;
                    }
                    break;
                case 'username':
                    aVal = (a.username || '').toLowerCase();
                    bVal = (b.username || '').toLowerCase();
                    comparison = sortOrder === 'asc'
                        ? aVal.localeCompare(bVal)
                        : bVal.localeCompare(aVal);
                    break;
                case 'credits':
                    aVal = a.credits || 0;
                    bVal = b.credits || 0;
                    comparison = sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
                    break;
                case 'balance':
                    aVal = a.balance || 0;
                    bVal = b.balance || 0;
                    comparison = sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
                    break;
                case 'company':
                    aVal = (a.company_name || a.manager || '').toLowerCase();
                    bVal = (b.company_name || b.manager || '').toLowerCase();
                    comparison = sortOrder === 'asc'
                        ? aVal.localeCompare(bVal)
                        : bVal.localeCompare(aVal);
                    break;
                default:
                    aVal = a.global_id || 0;
                    bVal = b.global_id || 0;
                    comparison = sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
            }

            return comparison;
        });

        return sorted;
    }, [userData, sortField, sortOrder]);

    const handleEditClick = (user) => {
        setUserToEdit(user);
        setIsCreatorUser(user.user_type === 'creator');
        setEditDialogOpen(true);
    };

    const getDisplayName = (user) => {
        if (user.user_type === 'creator') {
            return user.display_name || user.username;
        }
        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
        return fullName || user.username;
    };

    const getLastLogin = (user) => {
        if (!user.last_login) return 'Never';
        try {
            return new Date(user.last_login).toLocaleString();
        } catch {
            return 'Invalid Date';
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setUserTypeFilter('all');
        setCompanyFilter('');
        setStatusFilter('all');
        setSortField('created_at');
        setSortOrder('desc');
        setPage(0); // Reset to first page
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to first page
    };

    // Reset to first page when filters change
    const handleFilterChange = (setter) => (value) => {
        setter(value);
        setPage(0);
    };

    // Show loading state (only on initial load)
    if (isLoading && !userData.length) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
                flexDirection: 'column',
                gap: 2
            }}>
                <CircularProgress size={60} />
                <Typography variant="h6" color="text.secondary">
                    Loading user data...
                </Typography>
            </Box>
        );
    }

    // Show error state
    if (error) {
        return (
            <Box sx={{ padding: 2 }}>
                <AlertDialog alertState={alertHk.dialogState} />
                <Paper sx={{ padding: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="error" gutterBottom>
                        Failed to load user data
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {error.message}
                    </Typography>
                    <Button variant="contained" onClick={() => refetch()}>
                        Retry
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ padding: 2 }}>
            <AlertDialog alertState={alertHk.dialogState} />
            
            <Typography variant="h4" sx={{ marginBottom: 2 }}>
                User Administration
            </Typography>

            {/* Filters */}
            <Paper elevation={4} sx={{ padding: 2, marginBottom: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', marginBottom: 2 }}>
                    <TextField
                        label="Search"
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => handleFilterChange(setSearchTerm)(e.target.value)}
                        sx={{ minWidth: 250 }}
                        placeholder="Username, email, name, ID..."
                    />

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>User Type</InputLabel>
                        <Select
                            value={userTypeFilter}
                            label="User Type"
                            onChange={(e) => handleFilterChange(setUserTypeFilter)(e.target.value)}
                        >
                            <MenuItem value="all">All Types</MenuItem>
                            <MenuItem value="normal">Normal Users</MenuItem>
                            <MenuItem value="creator">Creator Users</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Status"
                            onChange={(e) => handleFilterChange(setStatusFilter)(e.target.value)}
                        >
                            <MenuItem value="all">All Status</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Company/Agency</InputLabel>
                        <Select
                            value={companyFilter}
                            label="Company/Agency"
                            onChange={(e) => handleFilterChange(setCompanyFilter)(e.target.value)}
                        >
                            <MenuItem value="">All Companies</MenuItem>
                            {uniqueCompanies.map(company => (
                                <MenuItem key={company} value={company}>
                                    {company}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Sort By</InputLabel>
                        <Select
                            value={sortField}
                            label="Sort By"
                            onChange={(e) => setSortField(e.target.value)}
                        >
                            <MenuItem value="created_at">Sign Up Date</MenuItem>
                            <MenuItem value="last_login">Last Login</MenuItem>
                            <MenuItem value="username">Username</MenuItem>
                            <MenuItem value="company">Company</MenuItem>
                            <MenuItem value="credits">Credits</MenuItem>
                            <MenuItem value="balance">Balance</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Order</InputLabel>
                        <Select
                            value={sortOrder}
                            label="Order"
                            onChange={(e) => setSortOrder(e.target.value)}
                        >
                            <MenuItem value="desc">Newest First</MenuItem>
                            <MenuItem value="asc">Oldest First</MenuItem>
                        </Select>
                    </FormControl>

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={clearFilters}
                    >
                        Clear Filters
                    </Button>

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => refetch()}
                        disabled={isLoading}
                    >
                        Refresh
                    </Button>

                    <Box sx={{ flexGrow: 1 }} />

                    <Chip
                        label={`Showing ${sortedUsers.length} of ${totalCount} users`}
                        color="primary"
                        variant="outlined"
                    />
                </Box>
            </Paper>

            {/* Unified Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>ID</strong></TableCell>
                            <TableCell><strong>Type</strong></TableCell>
                            <TableCell><strong>Username</strong></TableCell>
                            <TableCell><strong>Display Name</strong></TableCell>
                            <TableCell><strong>Email</strong></TableCell>
                            <TableCell><strong>Company/Agency</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Credits</strong></TableCell>
                            <TableCell><strong>Balance</strong></TableCell>
                            <TableCell><strong>Created</strong></TableCell>
                            <TableCell><strong>Last Login</strong></TableCell>
                            <TableCell><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedUsers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={12} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ padding: 4 }}>
                                        No users found matching the current filters
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            sortedUsers.map((user) => (
                                <TableRow key={`${user.user_type}-${user.global_id}`} hover>
                                    <TableCell>{user.global_id}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={user.user_type}
                                            size="small"
                                            color={user.user_type === 'creator' ? 'primary' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>{user.username}</TableCell>
                                    <TableCell>{getDisplayName(user)}</TableCell>
                                    <TableCell>{user.email || 'N/A'}</TableCell>
                                    <TableCell>
                                        {user.company_name || user.manager || 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={user.status ? 'Active' : 'Inactive'}
                                            size="small"
                                            color={user.status ? 'success' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>{user.credits?.toFixed(2) || '0.00'}</TableCell>
                                    <TableCell>${user.balance?.toFixed(2) || '0.00'}</TableCell>
                                    <TableCell>
                                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                                    </TableCell>
                                    <TableCell>{getLastLogin(user)}</TableCell>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleEditClick(user)}
                                        >
                                            Edit
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>

                {/* Pagination */}
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={[25, 50, 100, 200]}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                    }
                />
            </TableContainer>

            {/* Edit Dialogs */}
            {userToEdit && (
                isCreatorUser ? (
                    <EditCreditsDialog
                        open={editDialogOpen}
                        onClose={(updated) => {
                            setEditDialogOpen(false);
                            setUserToEdit(null);
                            if (updated) {
                                refetch();
                            }
                        }}
                        userInfo={userToEdit}
                    />
                ) : (
                    <EditUserDialog
                        open={editDialogOpen}
                        onClose={(updated) => {
                            setEditDialogOpen(false);
                            setUserToEdit(null);
                            if (updated) {
                                refetch();
                            }
                        }}
                        userInfo={userToEdit}
                    />
                )
            )}
        </Box>
    );
};

export default UnifiedUserAdminView;